import { useContext } from "react";
import { Box, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import { RopChart } from "../../../../main/components/RopChart";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { RootCauseAnalysisData } from "../../types";

export type AnaliseCausaRaizCascataChartProps = {
    data: RootCauseAnalysisData;
    height?: number | string;
};

export default function AnaliseCausaRaizCascataChart({ data, height }: AnaliseCausaRaizCascataChartProps) {
    const { theme } = useContext(MainContext);

    const theoreticalPredictedEnergy = data.theoreticalPredictedEnergy ?? 0;
    const idealEnergy = data.idealEnergy;
    const realEnergy = data.generatedEnergy;
    const predictedEnergy = data.predictedEnergy ?? 0;
    const theoreticalGap = data.theoreticalEnergyGap ?? (theoreticalPredictedEnergy - idealEnergy);
    const predictedGap = data.predictedEnergyGap ?? (predictedEnergy - realEnergy);

    const theoreticalPredictedIndex = 0;
    const theoreticalGapIndex = 1;
    const idealIndex = 2;
    const firstLossIndex = 3;
    const gainsIndex = 9;
    const realIndex = 10;
    const predictedGapIndex = 11;
    const predictedIndex = 12;

    // Criar categorias de perdas para o gráfico de cascata
    const categories = [
        {
            name: "Perdas Ópticas/\nIrradiância",
            value: data.opticalAndIrradiationLosses
        },
        {
            name: "Perdas DC\n(Mismatch/Cabos) ",
            value: data.conversionAndDCModuleLosses
        },
        {
            name: "Perdas nos\nInversores",
            value: data.inverterEfficiencyLosses
        },
        {
            name: "Perdas no\ntrafo ",
            value: data.transformerEfficiencyLosses
        },
        {
            name: "Indisponibilidade\nInv.",
            value: data.inverterUnavailabilityLoss
        },
        {
            name: "Indisponibilidade\nString",
            value: data.stringUnavailabilityLoss
        },
        {
            //TODO: a implementação acima foi realizada para a release de dia 09.04.26, por dados estranhos gerados a partir da implementaão da regra de outros ganhos / outras perdas, necessário realizar validação novamente

            name: data.energyBalance < -0.000001 ? "Outros\nGanhos" : "Outras\nPerdas",
            value: data.energyBalance
        }
    ];


    const ordemEixoXGrafico = [
        "Energia\nPrevista\nTeórica",
        "Diferença\nTeórica",
        "Energia\nTeórica",
        ...categories.map(cat => cat.name),
        "Energia\nGerada\nReal",
        "Diferença\nPrevista",
        "Energia\nPrevista"
    ];

    const energiaEixoYGrafico = [
        theoreticalPredictedEnergy,
        "-",
        idealEnergy,
        ...categories.map(() => "-"),
        realEnergy,
        "-",
        predictedEnergy
    ];

    const perdasEixoYGrafico = [
        "-",
        Math.abs(theoreticalGap),
        "-",
        ...categories.map(cat => Math.abs(cat.value ?? 0)),
        "-",
        Math.abs(predictedGap),
        "-"
    ];

    let valorAtual = idealEnergy;
    const lossPlaceholders = categories.map(cat => {
        const catValue = cat.value ?? 0;
        if (catValue < 0) {
            const inicio = valorAtual;
            valorAtual -= catValue;
            return inicio;
        }
        valorAtual -= catValue;
        return valorAtual;
    });

    const dadosEixoYDiferencaGrafico = [
        0,
        Math.min(theoreticalPredictedEnergy, idealEnergy),
        0,
        ...lossPlaceholders,
        0,
        Math.min(realEnergy, predictedEnergy),
        0
    ];

    const formatMWh = (value: number) =>
        value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const tooltipDescriptions: Record<number, string> = {
        [theoreticalPredictedIndex]: "Energia prevista teórica, calculada com GlobInc, área dos módulos e eficiência.",
        [theoreticalGapIndex]: "Diferença entre a energia prevista teórica e a energia teórica.",
        [idealIndex]: "Energia máxima estimada com base na irradiância disponível e nos parâmetros de projeto da usina, sem considerar perdas do sistema.",
        3: "Perdas associadas à incidência da radiação nos módulos, incluindo efeitos ópticos, sujeira (soiling), ângulo de incidência e variações de irradiância.",
        4: "Perdas no lado DC causadas por diferenças elétricas entre módulos (mismatch), resistências em cabos, conexões e pequenas variações entre strings.",
        5: "Perdas decorrentes da conversão de corrente contínua (DC) para corrente alternada (AC) nos inversores devido à eficiência de conversão.",
        6: "Perdas elétricas associadas à transformação de tensão nos transformadores, incluindo perdas em cobre e no núcleo magnético.",
        7: "Energia não gerada devido a períodos em que inversores estiveram indisponíveis por falhas, proteção, manutenção ou desligamento operacional.",
        8: "Perdas causadas por strings ou conjuntos de módulos fora de operação devido a falhas, desconexões ou problemas no campo fotovoltaico.",
        [gainsIndex]: "Ganhos ou perdas de geração decorrentes de condições operacionais não diagnosticadas.",
        [realIndex]: "Energia efetivamente produzida e entregue pela usina após a consideração de todas as perdas ou ganhos do sistema.",
        [predictedGapIndex]: "Diferença entre a energia prevista e a energia gerada real.",
        [predictedIndex]: "Energia prevista (Egrid) do período, conforme o input mensal da usina.",
    };

    const chartOption: EChartsOption = {
        backgroundColor: "transparent",
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow"
            },
            formatter: (params: any) => {
                const tar1 = params[1]; // Energia
                const tar2 = params[2]; // Perdas
                const dataIndex = tar1?.dataIndex ?? tar2?.dataIndex ?? 0;
                const description = tooltipDescriptions[dataIndex] ?? "";
                const descriptionHtml = description
                    ? `<div style="margin-top:6px;font-size:11px;color:#9CA3AF;max-width:220px;white-space:normal;">${description}</div>`
                    : "";

                if (tar1?.value !== "-") {
                    const tar1ValuePtBr = typeof tar1.value === "number"
                        ? tar1.value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : tar1.value;
                    return `<strong>${tar1.name.replace(/\n/g, " ")}</strong><br/>${tar1.seriesName}: <strong>${tar1ValuePtBr} MWh</strong>${descriptionHtml}`;
                } else if (tar2?.value !== "-") {
                    const tar2ValuePtBr = typeof tar2.value === "number"
                        ? tar2.value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : tar2.value;
                    const isTheoreticalGap = tar2.dataIndex === theoreticalGapIndex;
                    const isPredictedGap = tar2.dataIndex === predictedGapIndex;
                    const isGain = tar2.dataIndex === gainsIndex && tar2.name.includes("Ganhos");
                    const signedGap = isTheoreticalGap ? theoreticalGap : predictedGap;
                    const label = isTheoreticalGap || isPredictedGap ? "Diferença" : isGain ? "Ganhos" : "Perdas";
                    const signedValue = isTheoreticalGap || isPredictedGap ? signedGap : isGain ? tar2.value : -tar2.value;
                    const sign = typeof signedValue === "number" && signedValue > 0 ? "+" : "";
                    const displayValue = typeof signedValue === "number" ? formatMWh(signedValue) : tar2ValuePtBr;
                    return `<strong>${tar2.name.replace(/\n/g, " ")}</strong><br/>${label}: <strong>${sign}${displayValue} MWh</strong>${descriptionHtml}`;
                }
                return "";
            },
            backgroundColor: theme?.cardBoxBackgroudColor || "#1F2937",
            borderColor: theme?.cardBorderColor || "#374151",
            textStyle: {
                color: theme?.primaryTextColor || "#F9FAFB"
            }
        },
        grid: {
            left: "3%",
            right: "3%",
            bottom: "0%",
            top: "9%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            splitLine: { show: false },
            data: ordemEixoXGrafico,
            axisLine: {
                lineStyle: {
                    color: theme?.cardBorderColor || "#E5E7EB",
                },
            },
            axisLabel: {
                color: theme?.secondaryTextColor || "#6B7280",
                fontSize: 10,
                interval: 0,
                rotate: 0
            },
            axisTick: {
                show: false
            }
        },
        yAxis: {
            type: "value",
            axisLine: { show: false },
            axisTick: { show: false },
            axisLabel: {
                color: theme?.secondaryTextColor || "#6B7280",
                fontSize: 11
            },
            splitLine: {
                show: true,
                lineStyle: {
                    color: theme?.cardBorderColor || "#374151",
                    type: "dashed",
                    opacity: 0.5
                }
            }
        },
        series: [
            {
                name: "Placeholder",
                type: "bar",
                stack: "Total",
                itemStyle: {
                    borderColor: "transparent",
                    color: "transparent"
                },
                emphasis: {
                    itemStyle: {
                        color: (params: any) => {
                            if (params.dataIndex === gainsIndex && params.name.includes("Ganhos")) return "#10B981";
                            if (params.dataIndex === theoreticalGapIndex) return "#FACC15";
                            if (params.dataIndex === predictedGapIndex) return "#CA8A04";
                            return "#EF4444";
                        }
                    }
                },
                data: dadosEixoYDiferencaGrafico
            },
            {
                name: "Energia",
                type: "bar",
                stack: "Total",
                itemStyle: {
                    color: (params: any) => {
                        if (params.dataIndex === theoreticalPredictedIndex) return "#64748B";
                        if (params.dataIndex === idealIndex) return "#9CA3AF";
                        if (params.dataIndex === predictedIndex) return "#059669";
                        return "#34D399";
                    }
                },
                label: {
                    show: true,
                    position: "inside",
                    color: theme?.primaryTextColor || "#F9FAFB",
                    fontSize: 10,
                    formatter: (params: any) => {
                        return typeof params.value === "number"
                            ? params.value.toLocaleString("pt-BR", {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1
                            })
                            : `${params.value} MWh`;
                    }
                },
                data: energiaEixoYGrafico
            },
            {
                name: "Perdas",
                type: "bar",
                stack: "Total",
                itemStyle: {
                    color: (params: any) => {
                        if (params.dataIndex === theoreticalGapIndex) return "#FACC15";
                        if (params.dataIndex === predictedGapIndex) return "#CA8A04";
                        if (params.dataIndex === gainsIndex && params.name.includes("Ganhos")) return "#10B981";
                        if (params.dataIndex >= firstLossIndex && params.dataIndex <= firstLossIndex + 3) return "#60A5FA";
                        return "#f59e0b";
                    }
                },
                label: {
                    show: true,
                    position: "inside",
                    color: theme?.primaryTextColor || "#F9FAFB",
                    fontSize: 10,
                    formatter: (params: any) => {
                        if (typeof params.value !== "number") return `${params.value} MWh`;
                        if (params.dataIndex === theoreticalGapIndex || params.dataIndex === predictedGapIndex) {
                            const gap = params.dataIndex === theoreticalGapIndex ? theoreticalGap : predictedGap;
                            const sign = gap > 0 ? "+" : "";
                            return sign + gap.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                        }
                        const prefix = params.dataIndex === gainsIndex && params.name.includes("Ganhos") ? "" : "-";
                        return prefix + params.value.toLocaleString("pt-BR", {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1
                        });
                    }
                },
                data: perdasEixoYGrafico
            }
        ]
    };

    return (
        <Box
            sx={{
                backgroundColor: theme.cardBoxBackgroudColor,
                borderRadius: "8px",
                padding: "16px",
                height: height || 350,
                display: "flex",
                flexDirection: "column"
            }}
        >
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", paddingX: "25px" }}>
                <Typography sx={{ fontSize: "14px", fontWeight: "500", marginBottom: "8px" }}>
                    Cascata de Perdas
                </Typography>
                <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor, marginBottom: "16px" }}>
                    Energia prevista teórica → prevista
                </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, minHeight: "200px" }}>
                <RopChart option={chartOption} />
            </Box>

            {/* Legend */}
            <Box sx={{ display: "flex", justifyContent: "center", gap: "16px", marginTop: "16px", flexWrap: "wrap" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#64748B", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Energia Prevista Teórica</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#FACC15", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Diferença Prevista Teórica</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#9CA3AF", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Energia Teórica</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#34D399", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Energia Gerada</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#CA8A04", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Diferença Energia Prevista</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#059669", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Energia Prevista</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#60A5FA", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Perdas Previstas</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#f59e0b", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Perdas Gerenciáveis</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <Box sx={{ width: "10px", height: "10px", backgroundColor: "#10B981", borderRadius: "2px" }} />
                    <Typography sx={{ fontSize: "12px" }}>Ganhos</Typography>
                </Box>
            </Box>
        </Box>
    );
}
